// ===========================================================================
// ReHome Shelter — SuperAdmin Page (S2)
//
// Accessible only by super_admin role.
// Shows all shelters with aggregated stats (GDPR: numbers only, no PII).
// Allows shelter registration and admin creation.
// ===========================================================================

import React, { useState, useMemo } from 'react';
import {
  useShelters, useShelterAnimalCounts, useFinancialTransactions, useNetworkBalance,
  useSpeciesBreakdown, useStatusBreakdown, ANIMAL_STATUS_LABELS,
  formatHUF, supabase, AddressAutocompleteInput,
} from '@rehome/shared';
import type { AnimalStatus } from '@rehome/shared';
import { BreakdownDonut } from '../components/BreakdownDonut';
import {
  Building2, PawPrint, Plus, Users, TrendingUp, Wallet,
  MapPin, X, Loader2, AlertCircle, CheckCircle, Shield, Activity,
} from 'lucide-react';

// A megoszlás-diagramok színei. A státuszoknál a szín JELENT valamit — a
// gazdikereső zöld, az orvosi és a karantén figyelmeztető árnyalat —, hogy a
// diagram ránézésre is olvasható legyen, ne csak a jelmagyarázattal együtt.
const SPECIES_COLORS: Record<string, string> = {
  'Kutya': '#0d9488',
  'Macska': '#f59e0b',
  'Egyéb': '#6366f1',
};

const STATUS_COLORS: Record<string, string> = {
  Available: '#16a34a',
  AdoptionPending: '#f59e0b',
  Adopted: '#0d9488',
  Foster: '#6366f1',
  Medical: '#ef4444',
  StrayHold: '#8b5cf6',
  Quarantine: '#f97316',
};

// ─── SuperAdmin Page ──────────────────────────────────────────────────────

const SuperAdminPage: React.FC = () => {
  const { data: sheltersData, loading: sLoading } = useShelters();
  const { counts: animalCounts, total: totalAnimals, loading: aLoading } = useShelterAnimalCounts();
  const { data: txData, loading: tLoading } = useFinancialTransactions();
  const { data: speciesBreakdown, loading: spLoading } = useSpeciesBreakdown();
  const { data: statusBreakdown, loading: stLoading } = useStatusBreakdown();
  const { balance: networkBalance, loading: balanceLoading } = useNetworkBalance();
  const [showAddShelter, setShowAddShelter] = useState(false);

  const shelters = sheltersData ?? [];
  const transactions = txData ?? [];
  const loading = sLoading || aLoading || tLoading;

  // Aggregated stats per shelter
  const shelterStats = useMemo(() => {
    return shelters.map(s => {
      const animalCount = animalCounts.get(s.id) ?? 0;
      const shelterTx = transactions.filter(t => t.shelterId === s.id);
      const totalDonations = shelterTx
        .filter(t => t.category === 'Donation')
        .reduce((sum, t) => sum + t.amount, 0);
      const totalAdoptionFees = shelterTx
        .filter(t => t.category === 'Adoption')
        .reduce((sum, t) => sum + t.amount, 0);

      return {
        ...s,
        animalCount,
        occupancyRate: s.capacity > 0 ? Math.round((animalCount / s.capacity) * 100) : 0,
        totalDonations,
        totalAdoptionFees,
        totalFinancial: totalDonations + totalAdoptionFees,
      };
    });
  }, [shelters, animalCounts, transactions]);

  const speciesSlices = useMemo(
    () => speciesBreakdown.map(d => ({ key: d.species, label: d.species || 'Ismeretlen', count: d.count })),
    [speciesBreakdown]
  );

  const statusSlices = useMemo(
    () => statusBreakdown.map(d => ({
      key: d.status,
      label: ANIMAL_STATUS_LABELS[d.status as AnimalStatus] ?? d.status,
      count: d.count,
    })),
    [statusBreakdown]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-teal-600" />
            Superadmin Vezérlőpult
          </h1>
          <p className="text-gray-600 mt-1">
            Intézménykezelés — {shelters.length} menhely a hálózatban
          </p>
        </div>
        <button
          onClick={() => setShowAddShelter(true)}
          className="bg-teal-600 text-white px-5 py-3 rounded-xl font-bold hover:bg-teal-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Új Menhely Regisztrálása
        </button>
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          icon={<Building2 className="w-6 h-6" />}
          label="Regisztrált Menhelyek"
          value={shelters.length.toString()}
        />
        <StatCard
          icon={<PawPrint className="w-6 h-6" />}
          label="Összes Gondozott Állat"
          value={totalAnimals.toString()}
        />
        <StatCard
          icon={<Wallet className="w-6 h-6" />}
          label="Összesített Pénzügyi Egyenleg"
          value={balanceLoading ? '…' : formatHUF(networkBalance ?? 0)}
        />
      </div>

      {/* ── Megoszlások ────────────────────────────────────────────────
          A darabszámok fentebb megvannak; ez arra válaszol, hogy MIBŐL áll
          az az összeg. Az összegzést a szerver végzi (get_species_breakdown /
          get_status_breakdown), tehát nem kell hozzá minden állat sorát
          letölteni. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BreakdownDonut
          title="Fajok megoszlása"
          icon={<PawPrint className="w-4 h-4 text-teal-600" />}
          data={speciesSlices}
          loading={spLoading}
          colors={SPECIES_COLORS}
        />
        <BreakdownDonut
          title="Státuszok megoszlása"
          icon={<Activity className="w-4 h-4 text-teal-600" />}
          data={statusSlices}
          loading={stLoading}
          colors={STATUS_COLORS}
        />
      </div>

      {/* Shelter List */}
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-teal-600" />
          Menhelyek ({shelters.length})
        </h2>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-500">
                <th className="px-6 py-3 font-medium">Menhely</th>
                <th className="px-6 py-3 font-medium">Helyszín</th>
                <th className="px-6 py-3 font-medium text-center">Kapacitás</th>
                <th className="px-6 py-3 font-medium text-center">Állatok</th>
                <th className="px-6 py-3 font-medium text-center">Telítettség</th>
                <th className="px-6 py-3 font-medium text-right">Pénzügyi Egyenleg</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {shelterStats.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">{s.name}</div>
                  </td>
                  <td className="px-6 py-4 text-gray-500">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {s.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center text-gray-700">{s.capacity}</td>
                  <td className="px-6 py-4 text-center font-semibold text-gray-900">{s.animalCount}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      s.occupancyRate > 80 ? 'bg-red-100 text-red-700' :
                      s.occupancyRate > 50 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {s.occupancyRate}%
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right font-mono text-sm text-gray-700">
                    {formatHUF(s.totalFinancial)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {shelters.length === 0 && (
          <div className="text-center py-12 text-gray-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Még nincs regisztrált menhely.</p>
          </div>
        )}
      </div>

      {/* Add Shelter Modal */}
      {showAddShelter && (
        <AddShelterModal onClose={() => setShowAddShelter(false)} />
      )}
    </div>
  );
};

// ─── Stat Card ────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex items-center gap-4">
    <div className="p-3 rounded-full bg-teal-100 text-teal-700">{icon}</div>
    <div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  </div>
);

// ─── Add Shelter Modal ────────────────────────────────────────────────────

/** Generate a URL-safe slug from a Hungarian shelter name (slug is NOT NULL UNIQUE in the DB). */
const slugify = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents (e.g. é → e, ő → o)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || `menhely-${Date.now()}`;

const AddShelterModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [name, setName] = useState('');
  const [location, setLocation] = useState('');
  // A shelters táblában van `lat`/`lng`, de ez az űrlap eddig sosem töltötte
  // ki — így minden itt létrehozott menhely koordináta nélkül került be, és
  // nem jelent meg a térképes nézetekben. A címjavaslat kiválasztása most
  // ezeket is megadja.
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [capacity, setCapacity] = useState(0);
  const [description, setDescription] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  /**
   * Az újonnan létrehozott menhely-admin belépési adatai. A
   * create-shelter-admin függvény e-mailt NEM küld róluk, ezért ez az
   * egyetlen alkalom, amikor az ideiglenes jelszó megjeleníthető.
   */
  const [credentials, setCredentials] = useState<{ email: string; tempPassword: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!credentials) return;
    try {
      await navigator.clipboard.writeText(
        `Belépés: ${credentials.email}\nIdeiglenes jelszó: ${credentials.tempPassword}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Vágólap megtagadva — az adat a képernyőn látszik, kézzel másolható.
    }
  };

  const closeAndRefresh = () => { onClose(); window.location.reload(); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      // slug is NOT NULL UNIQUE in the schema — without it the insert fails.
      // Suffix with a short timestamp fragment to avoid collisions.
      const slug = `${slugify(name)}-${Date.now().toString(36).slice(-4)}`;
      // `.select()` kell: az admin létrehozásához szükségünk van az új
      // menhely azonosítójára (korábban a beszúrás nem adott vissza semmit,
      // ezért az admin e-mail mező nem is tudott működni).
      const { data: newShelter, error: shelterErr } = await supabase
        .from('shelters')
        .insert({
          name,
          slug,
          location,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          capacity,
          description,
          image_url: null,
        })
        .select('id')
        .single();

      if (shelterErr) throw new Error(shelterErr.message);

      // Admin e-mail nélkül itt végzünk: a menhely megvan, a `trg_new_shelter_admin`
      // trigger pedig a létrehozót (a super_admint) felvette staffnak, tehát a
      // munkatársak később a menhely vezérlőpultjáról is hozzáadhatók.
      if (!adminEmail.trim()) {
        setSuccess(true);
        setSuccessMsg(`A(z) "${name}" menhely sikeresen létrehozva! Admin nélkül jött létre — munkatársat a menhely vezérlőpultján adhatsz hozzá.`);
        setTimeout(() => { setSuccess(false); onClose(); window.location.reload(); }, 3000);
        return;
      }

      const { data: fnData, error: fnError } = await supabase.functions.invoke('create-shelter-admin', {
        body: { email: adminEmail.trim(), shelterId: newShelter.id, role: 'Admin' },
      });

      if (fnError || fnData?.error) {
        // A menhely MÁR létrejött — ezt nem szabad elhallgatni, különben a
        // felhasználó azt hinné, semmi nem történt, és újra próbálkozna.
        throw new Error(
          `A(z) "${name}" menhely létrejött, de az admin fiók nem: ` +
          `${fnData?.error || fnError?.message}. A munkatársat a menhely vezérlőpultján veheted fel.`
        );
      }

      setCredentials({ email: fnData?.email ?? adminEmail.trim(), tempPassword: fnData?.tempPassword ?? '' });
    } catch (err: any) {
      setError(err.message || 'Hiba történt a menhely létrehozása során.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal-600" />
            Új Menhely Regisztrálása
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {credentials ? (
          <div className="p-6 space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-xl flex items-start gap-2 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>A menhely és az admin fiók elkészült. Add át az alábbi belépési adatokat.</span>
            </div>

            {/* A rendszer nem küld e-mailt a belépési adatokról, ezért ez az
                egyetlen alkalom, amikor az ideiglenes jelszó látható. */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              <p className="font-semibold">Írd ki most — később már nem jeleníthető meg.</p>
              <p className="text-amber-800/80 text-xs mt-0.5">
                Ha elveszne, az admin a bejelentkezésnél az „Elfelejtett jelszó?” funkcióval tud újat kérni.
              </p>
            </div>

            <div className="space-y-2">
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Email</span>
                <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm break-all">
                  {credentials.email}
                </code>
              </div>
              <div>
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Ideiglenes jelszó</span>
                <code className="block bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono break-all">
                  {credentials.tempPassword || '— a függvény nem adott vissza jelszót —'}
                </code>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Első belépéskor a rendszer kötelezően új jelszó beállítására viszi.
            </p>

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={handleCopy}
                className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50">
                {copied ? 'Kimásolva ✓' : 'Adatok másolása'}
              </button>
              <button type="button" onClick={closeAndRefresh}
                className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700">
                Kész
              </button>
            </div>
          </div>
        ) : (
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl flex items-center gap-2 text-sm">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {successMsg}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Menhely neve *</label>
            <input
              type="text" required value={name} onChange={e => setName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="Pl. Budapesti Állatmenhely"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Helyszín *</label>
            <AddressAutocompleteInput
              value={location}
              onChange={address => {
                setLocation(address);
                // Kézi átírás után a korábbi koordináta már máshova mutatna.
                setCoords(null);
              }}
              onPlaceSelected={({ address, lat, lng }) => { setLocation(address); setCoords({ lat, lng }); }}
              placeholder="Pl. Budapest, XIII. kerület"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kapacitás (férőhely) *</label>
            <input
              type="number" required min={1} value={capacity || ''} onChange={e => setCapacity(Number(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="Pl. 50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Leírás</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
              rows={3}
              placeholder="Rövid bemutatkozás a menhelyről..."
            />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Shelter Admin email címe <span className="text-gray-400 font-normal">(opcionális)</span>
            </label>
            <input
              type="email" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none"
              placeholder="admin@menhely.hu"
            />
            <p className="text-xs text-gray-400 mt-1">
              Ha kitöltöd, a menhely létrehozásával együtt elkészül az admin fiók is, és a
              belépési adatokat rögtön megmutatjuk. Üresen hagyva a munkatársakat később, a
              menhely vezérlőpultján veheted fel.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Mégse
            </button>
            <button type="submit" disabled={submitting}
              className="flex-1 py-3 bg-teal-600 text-white rounded-xl font-bold hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Menhely Létrehozása
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
};

export default SuperAdminPage;
